import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    setupIdleTimer();
    return () => cleanupIdleTimer();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (googleToken) => {
    try {
      const response = await authApi.login(googleToken);
      localStorage.setItem('token', response.token);
      setUser(response.user);
      resetIdleTimer();
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    cleanupIdleTimer();
  };

  // Idle timeout mechanism
  let idleTimer;
  const IDLE_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    if (user) {
      idleTimer = setTimeout(() => {
        console.log('User idle for 10 minutes, logging out');
        logout();
        window.location.href = '/login';
      }, IDLE_TIMEOUT);
    }
  };

  const handleActivity = () => {
    resetIdleTimer();
  };

  const setupIdleTimer = () => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });
    resetIdleTimer();
  };

  const cleanupIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.removeEventListener(event, handleActivity);
    });
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasRole: (...roles) => user && roles.includes(user.role)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
